/**
 * Holt Antworten zurück, die in einer Einreichung stehen, aber zu keiner Frage
 * des heutigen Fragebogens mehr gehören.
 *
 * Die Ergebnisse-Seite läuft über die Fragen des Fragebogens und holt zu jeder
 * ihre Antwort. Das heißt im Umkehrschluss: verschwindet eine Frage-ID, wird
 * ihre Antwort nirgends mehr gerendert — nicht als Lücke, nicht als Hinweis,
 * sie ist schlicht unsichtbar. Genau das passiert, wenn in einen Fragebogen mit
 * bestehenden Bewerbungen eine Vorlage neu geladen oder JSON importiert wird:
 * jede Frage bekommt eine frische ID, und ganze Seiten wirken unbeantwortet.
 *
 * Drei Quellen kennen den Wortlaut einer Frage, und alle drei liegen in der
 * Einreichung selbst, nicht im Fragebogen:
 *   1. `detailedAnswers` — der Abzug, den das Formular beim Absenden anlegt:
 *      Text, Typ und Optionen je Frage. Seit `detailed_answers` existiert.
 *   2. `answerLog.interactions[id].questionText` — pro berührter Frage ihr Text.
 *      Gibt es seit jeher, auch für Altdatensätze ohne Abzug.
 *   3. Nichts davon: dann bleibt die ID, und die Antwort steht wenigstens da.
 */

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/** Leere Hüllen sind keine geretteten Antworten — sie würden die Ansicht nur zumüllen. */
export const hasAnswerContent = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) {
    return value.some((entry) =>
      isPlainObject(entry) ? Boolean(entry.value || entry.label) : hasAnswerContent(entry)
    );
  }
  if (isPlainObject(value)) {
    if (Array.isArray(value.fileIds)) return value.fileIds.length > 0;
    return Object.keys(value).length > 0;
  }
  return true;
};

/** Ohne Abzug und ohne Log bleibt nur die Form der Antwort, um den Typ zu raten. */
const inferType = (value) => {
  if (isPlainObject(value) && Array.isArray(value.fileIds)) return 'file-upload';
  if (Array.isArray(value)) {
    return value.some((entry) => isPlainObject(entry) && 'value' in entry) ? 'matrix' : 'multiple-choice';
  }
  return 'text';
};

/** Der Abzug liegt nach Standard- und eigenen Fragen getrennt vor, die Reihenfolge zählt. */
const snapshotEntries = (detailedAnswers) => {
  if (!isPlainObject(detailedAnswers)) return [];
  return [
    ...(Array.isArray(detailedAnswers.defaultQuestions) ? detailedAnswers.defaultQuestions : []),
    ...(Array.isArray(detailedAnswers.customQuestions) ? detailedAnswers.customQuestions : []),
  ].filter((entry) => entry && entry.id);
};

/**
 * Jede Antwort der Einreichung, zu der es im Fragebogen keine Frage mehr gibt —
 * als Frage-Objekt, damit die Ergebnisse-Seite sie mit ihrem normalen Renderer
 * anzeigen kann.
 *
 * `knownQuestionIds` sind alle IDs, die der Fragebogen heute kennt (Standard-
 * fragen und alle Seiten zusammen).
 */
export function recoverOrphanAnswers(submission, knownQuestionIds = []) {
  const answers = isPlainObject(submission?.answers) ? submission.answers : {};
  const known = knownQuestionIds instanceof Set ? knownQuestionIds : new Set(knownQuestionIds);

  const snapshot = new Map(snapshotEntries(submission?.detailedAnswers).map((entry) => [entry.id, entry]));
  const snapshotOrder = [...snapshot.keys()];
  const interactions = isPlainObject(submission?.answerLog?.interactions)
    ? submission.answerLog.interactions
    : {};

  const orphanIds = Object.keys(answers).filter(
    (id) => !known.has(id) && hasAnswerContent(answers[id])
  );

  // Der Abzug kennt die ursprüngliche Reihenfolge des Fragebogens; alles, was
  // nur im Log steht, hängt sich in der Reihenfolge der Antworten hinten an.
  orphanIds.sort((a, b) => {
    const indexA = snapshotOrder.indexOf(a);
    const indexB = snapshotOrder.indexOf(b);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return orphanIds.map((id) => {
    const fromSnapshot = snapshot.get(id);
    const textFromLog = String(interactions[id]?.questionText || '').trim();
    const text = String(fromSnapshot?.text || '').trim() || textFromLog;
    const value = answers[id];

    return {
      id,
      text: text || `Frage ohne Text (${id})`,
      type: fromSnapshot?.type || inferType(value),
      options: Array.isArray(fromSnapshot?.options) ? fromSnapshot.options : [],
      // Woher der Wortlaut stammt — "none" heißt: nur die ID, der Text ist weg.
      source: fromSnapshot?.text ? 'snapshot' : textFromLog ? 'log' : 'none',
      recovered: true,
    };
  });
}

/** Alle Frage-IDs, die ein Fragebogen heute kennt. */
export function collectKnownQuestionIds(defaultQuestions = [], questionPages = []) {
  const ids = new Set();
  for (const question of Array.isArray(defaultQuestions) ? defaultQuestions : []) {
    if (question?.id) ids.add(question.id);
  }
  for (const page of Array.isArray(questionPages) ? questionPages : []) {
    for (const question of Array.isArray(page?.questions) ? page.questions : []) {
      if (question?.id) ids.add(question.id);
    }
  }
  return ids;
}
